import '@material/web/textfield/outlined-text-field.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/text-button.js';

import { api, ApiError, type AttributionMember, type DisplayTask } from '../api.js';
import { ce, el, clear, avatarEl, fmtMinutes, type TextFieldEl } from '../dom.js';
import { t } from '../i18n.js';
import { icons } from '../icons.js';
import { sheet } from '../components/sheet.js';
import { renderToday } from './today.js';

// The shared-display view (ADR-0049): a no-privilege kiosk screen for a device
// the whole household shares. It lists everyone-visible open tasks; completing
// one is two taps — tap done (rendered as a checkmark + ellipsis to signal it
// opens a chooser), then tap who did it. A member with a PIN enters it then;
// members without one (typically kids) complete in two taps. The task row is
// the same as the normal list — only the done glyph differs (per the human).
//
// ADR-0061 adds the Today board above that list, in display mode: tap a member
// in its header row to act as them (PIN first if they have one), then tap their
// chips. Same two-tap shape, same on-behalf API — the board is a second way in,
// not a replacement, so the ADR-0049 list below is untouched.

function iconButton(cls: string, label: string, iconNode: HTMLElement, onclick: (ev: Event) => void): HTMLElement {
  return el('button', { class: `icon-btn ${cls}`, type: 'button', 'aria-label': label, title: label, onclick }, iconNode);
}

export function renderDisplay(onExit: () => void): HTMLElement {
  const root = el('div', {});
  const error = el('p', { class: 'error' });
  const list = el('div', { class: 'card', id: 'display-task-list' });

  // The board and the list show the same tasks, so a completion on either
  // has to refresh the other or one of them lies (ADR-0061). Board → list is
  // `onChanged`; list → board is this handle. No loop: each direction only
  // refreshes the OTHER surface's data.
  let reloadBoard: () => void = () => {};
  const board = renderToday({
    display: true,
    onChanged: () => load(),
    bindReload: (reload) => {
      reloadBoard = reload;
    },
  });

  // One attribution sheet, repopulated per task (ADR-0047 sheet component).
  const picker = sheet({ id: 'attribution', title: t('displayWhoTitle'), closeLabel: t('close'), variant: 'bottom' });
  const sheetError = el('p', { class: 'error' });

  function completeAs(task: DisplayTask, member: AttributionMember, pin?: string): void {
    sheetError.textContent = '';
    const body: Record<string, unknown> = { asMemberId: member.id };
    if (pin) body.pin = pin;
    api
      .post(`/api/tasks/${task.id}/complete`, body)
      .then(() => {
        picker.close();
        load();
        reloadBoard();
      })
      .catch((err: Error) => {
        // A wrong PIN keeps the sheet open with the message inside it; the
        // 403 for a PIN member with no pin is handled by showing the prompt
        // (see showMemberChoices), so this branch is a genuine failure.
        sheetError.textContent = err instanceof ApiError && err.status === 403 ? t('displayWrongPin') : err.message;
      });
  }

  // Tap 2 (PIN members only): the member's own PIN, reusing the picker's
  // exact unlock pattern (ADR-0006/0049).
  function showPinPrompt(task: DisplayTask, member: AttributionMember): void {
    clear(picker.body);
    const pinBack = ce('md-text-button', { type: 'button' }, t('pickerBack'));
    pinBack.addEventListener('click', () => showMemberChoices(task));
    const pin = ce('md-outlined-text-field', {
      id: 'attribution-pin', label: t('pickerEnterPin'), type: 'password', inputmode: 'numeric',
    }) as TextFieldEl;
    picker.body.append(
      el(
        'form',
        {
          onsubmit: (ev: Event) => {
            ev.preventDefault();
            completeAs(task, member, pin.value);
          },
        },
        el('div', { class: 'attribution-pin-head' }, avatarEl(member.name, member.avatar, member.color, 40), el('strong', {}, member.name)),
        el('label', { for: 'attribution-pin' }, t('pickerEnterPin')),
        pin,
        sheetError,
        el(
          'div',
          { class: 'row' },
          ce('md-filled-button', { type: 'submit' }, t('pickerUnlock')),
          pinBack,
        ),
      ),
    );
    pin.focus();
  }

  // Tap 2: who did it — eligible members as avatar buttons that wrap to as
  // many rows as needed (reuses the picker grid; verified at 375px / 5 members).
  function showMemberChoices(task: DisplayTask): void {
    sheetError.textContent = '';
    clear(picker.body);
    picker.body.append(
      el('p', { class: 'muted' }, t('displayWhoDidIt', { title: task.title })),
      el(
        'div',
        { class: 'picker' },
        ...task.eligibleMembers.map((m) =>
          el(
            'div',
            {
              class: 'profile',
              role: 'button',
              tabindex: '0',
              'data-attribution-member': m.name,
              onclick: () => (m.hasPin ? showPinPrompt(task, m) : completeAs(task, m)),
              onkeydown: (ev: Event) => {
                const key = (ev as KeyboardEvent).key;
                if (key === 'Enter' || key === ' ') {
                  ev.preventDefault();
                  if (m.hasPin) showPinPrompt(task, m);
                  else completeAs(task, m);
                }
              },
            },
            avatarEl(m.name, m.avatar, m.color, 48),
            el('div', {}, m.name),
          ),
        ),
      ),
      sheetError,
    );
  }

  function openAttribution(task: DisplayTask): void {
    showMemberChoices(task);
    picker.open();
  }

  function skip(task: DisplayTask, button: HTMLButtonElement): void {
    button.disabled = true;
    api
      .post(`/api/tasks/${task.id}/skip`)
      .then(() => {
        load();
        reloadBoard();
      })
      .catch((err: Error) => {
        error.textContent = err.message;
        load();
      });
  }

  function displayRow(task: DisplayTask): HTMLElement {
    const boosted = task.currentBoostMultiplier > 1;
    const actions = el(
      'div',
      { class: 'task-actions' },
      iconButton('', t('tasksNotNeeded'), icons.notNeeded(), (ev) => skip(task, ev.currentTarget as HTMLButtonElement)),
      iconButton('primary', t('displayDone'), icons.doneMore(), () => openAttribution(task)),
    );
    return el(
      'div',
      { class: 'task', 'data-task': task.title },
      el(
        'div',
        { class: 'task-body' },
        el('div', { class: 'title' }, task.title),
        el(
          'div',
          { class: 'meta' },
          el('span', {}, fmtMinutes(task.currentPayoutMinutes)),
          boosted ? el('span', { class: 'pill boost' }, `↑ ${task.currentBoostMultiplier.toFixed(1)}×`) : null,
        ),
      ),
      actions,
    );
  }

  function load(): void {
    api
      .get<{ tasks: DisplayTask[] }>('/api/display/tasks')
      .then(({ tasks }) => {
        clear(list);
        list.classList.toggle('card', tasks.length > 0);
        if (tasks.length === 0) {
          list.append(el('p', { class: 'task-empty muted' }, t('tasksEmpty')));
          return;
        }
        for (const task of tasks) list.append(displayRow(task));
      })
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  }

  const exitBtn = ce('md-text-button', { id: 'display-exit' }, t('switchProfile'));
  exitBtn.addEventListener('click', onExit);
  const header = el(
    'div',
    { class: 'app-header spread' },
    el('h1', { class: 'display-title' }, t('displayTitle')),
    exitBtn,
  );

  root.append(
    header,
    error,
    // ADR-0049's list keeps the top of the screen, unchanged — it is the
    // shipped flow and this screen's h1 already names it. The board is
    // additive, under its own heading (ADR-0061).
    list,
    el('h2', { class: 'display-section' }, t('todayTitle')),
    board,
    picker.root,
  );
  load();
  return root;
}
